import type { ReactNode } from 'react'
import type { CartItem, CartProduct } from '@/types'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react'

import { CART_STORAGE_KEY, MAX_ITEM_QUANTITY } from '@/constants/site'

export interface CartSyncResult {
    removed: number
    changed: number
}

interface CartContextValue {
    items: Array<CartItem>
    hydrated: boolean
    count: number
    subtotal: number
    lastAddedAt: number
    addItem: (product: CartProduct, quantity?: number) => void
    setQuantity: (productId: string, quantity: number) => void
    removeItem: (productId: string) => void
    clear: () => void
    sync: () => Promise<CartSyncResult | null>
}

interface CartProviderProps {
    children: ReactNode
}

const CartContext = createContext<CartContextValue | null>(null)

// Layout effects run before the passive effects of child pages, so `sync()` called
// from a page effect always sees the latest items. Falls back to useEffect on the server.
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

export const clampQuantity = (quantity: number): number =>
    Math.min(MAX_ITEM_QUANTITY, Math.max(1, Math.floor(quantity) || 1))

const isCartItem = (value: unknown): value is CartItem => {
    if (!value || typeof value !== 'object') return false

    const item = value as Record<string, unknown>

    return typeof item.id === 'string'
        && typeof item.slug === 'string'
        && typeof item.name === 'string'
        && typeof item.price === 'number'
        && typeof item.quantity === 'number'
}

const parseStoredCart = (raw: string | null): Array<CartItem> => {
    if (!raw) return []

    try {
        const parsed: unknown = JSON.parse(raw)

        if (!Array.isArray(parsed)) return []

        return parsed.filter(isCartItem).map((item) => ({
            id: item.id,
            slug: item.slug,
            name: item.name,
            price: item.price,
            image: typeof item.image === 'string' ? item.image : null,
            image_alt: typeof item.image_alt === 'string' ? item.image_alt : null,
            in_stock: item.in_stock !== false,
            quantity: clampQuantity(item.quantity)
        }))
    }
    catch {
        return []
    }
}

export const CartProvider = ({ children }: CartProviderProps): ReactNode => {
    // Server-safe defaults; the saved cart is read once after mount.
    const [items, setItems] = useState<Array<CartItem>>([])
    const [hydrated, setHydrated] = useState(false)
    const [lastAddedAt, setLastAddedAt] = useState(0)
    const itemsRef = useRef<Array<CartItem>>([])

    useEffect(() => {
        let stored: string | null = null

        try {
            stored = window.localStorage.getItem(CART_STORAGE_KEY)
        }
        catch {
            stored = null
        }

        setItems(parseStoredCart(stored))
        setHydrated(true)

        const onStorage = (event: StorageEvent) => {
            if (event.key === CART_STORAGE_KEY) setItems(parseStoredCart(event.newValue))
        }

        window.addEventListener('storage', onStorage)

        return () => window.removeEventListener('storage', onStorage)
    }, [])

    useIsomorphicLayoutEffect(() => {
        itemsRef.current = items
    }, [items])

    useEffect(() => {
        if (!hydrated) return

        try {
            window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items))
        }
        catch {
            // Storage can be full or blocked (private mode); the cart still works for this visit.
        }
    }, [items, hydrated])

    const addItem = useCallback((product: CartProduct, quantity = 1) => {
        setItems((current) => {
            const existing = current.find((item) => item.id === product.id)

            if (existing) {
                return current.map((item) => item.id === product.id
                    ? { ...item, ...product, quantity: clampQuantity(item.quantity + quantity) }
                    : item)
            }

            return [...current, { ...product, quantity: clampQuantity(quantity) }]
        })

        setLastAddedAt(Date.now())
    }, [])

    const setQuantity = useCallback((productId: string, quantity: number) => {
        setItems((current) => current.map((item) => item.id === productId
            ? { ...item, quantity: clampQuantity(quantity) }
            : item))
    }, [])

    const removeItem = useCallback((productId: string) => {
        setItems((current) => current.filter((item) => item.id !== productId))
    }, [])

    const clear = useCallback(() => {
        setItems([])
    }, [])

    // Refreshes names, prices and stock from the store so the cart never shows stale data.
    const sync = useCallback(async (): Promise<CartSyncResult | null> => {
        const snapshot = itemsRef.current

        if (snapshot.length === 0) return { removed: 0, changed: 0 }

        try {
            const response = await fetch('/api/cart', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ids: snapshot.map((item) => item.id) })
            })

            if (!response.ok) return null

            const data = await response.json() as { products?: Array<CartProduct> }
            const fresh = new Map((data.products ?? []).map((product) => [product.id, product]))
            const requested = new Set(snapshot.map((item) => item.id))

            let removed = 0
            let changed = 0

            for (const item of snapshot) {
                const product = fresh.get(item.id)

                if (!product) removed += 1
                else if (product.price !== item.price || product.in_stock !== item.in_stock) changed += 1
            }

            setItems((current) => current.flatMap((item) => {
                const product = fresh.get(item.id)

                if (product) return [{ ...item, ...product, quantity: item.quantity }]

                return requested.has(item.id) ? [] : [item]
            }))

            return { removed, changed }
        }
        catch {
            return null
        }
    }, [])

    const value = useMemo<CartContextValue>(() => ({
        items,
        hydrated,
        count: items.reduce((total, item) => total + item.quantity, 0),
        subtotal: items.reduce((total, item) => total + item.price * item.quantity, 0),
        lastAddedAt,
        addItem,
        setQuantity,
        removeItem,
        clear,
        sync
    }), [items, hydrated, lastAddedAt, addItem, setQuantity, removeItem, clear, sync])

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    )
}

export const useCart = (): CartContextValue => {
    const context = useContext(CartContext)

    if (!context) throw new Error('useCart must be used inside CartProvider')

    return context
}
